// Documents on the vehicle card: the files that followed the vehicle from Fleet360 and new ones
// uploaded here. Encrypted at rest, every download logged.
import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { sv } from '@aldorock-fordon/shared/sv';
import { api, ApiError } from '../api.ts';
import { Table } from '../ui/Table.tsx';
import { TextField } from '../ui/Fields.tsx';
import { moment } from '../format.ts';

type Doc = {
  id: string;
  fileName: string;
  mimeType: string;
  sizeBytes: number;
  description: string | null;
  createdAt: string;
};

const size = (n: number) =>
  n < 1024
    ? `${n} B`
    : n < 1024 * 1024
      ? `${Math.round(n / 1024)} kB`
      : `${(n / 1024 / 1024).toFixed(1).replace('.', ',')} MB`;

export function DocumentsSection({
  vehicleId,
  canWrite,
}: {
  vehicleId: string;
  canWrite: boolean;
}) {
  const qc = useQueryClient();
  const docs = useQuery({
    queryKey: ['documents', vehicleId],
    queryFn: () => api<Doc[]>(`/vehicles/${vehicleId}/documents`),
  });
  const [file, setFile] = useState<File | null>(null);
  const [description, setDescription] = useState('');
  const [error, setError] = useState<string | undefined>();
  const [message, setMessage] = useState<string | null>(null);
  const refresh = () =>
    Promise.all([
      qc.invalidateQueries({ queryKey: ['documents', vehicleId] }),
      qc.invalidateQueries({ queryKey: ['vehicle', vehicleId] }),
    ]);
  const upload = useMutation({
    mutationFn: async () => {
      const body = new FormData();
      body.append('description', description);
      body.append('file', file!);
      return api(`/vehicles/${vehicleId}/documents`, { method: 'POST', body });
    },
    onSuccess: async () => {
      setFile(null);
      setDescription('');
      setError(undefined);
      setMessage(sv.documents.uploaded);
      await refresh();
    },
    onError: (e) =>
      setError(e instanceof ApiError && e.errors[0] ? e.errors[0].message : sv.errors.server),
  });
  const remove = useMutation({
    mutationFn: (id: string) => api(`/documents/${id}`, { method: 'DELETE' }),
    onSuccess: refresh,
  });
  return (
    <section className="block" aria-labelledby="documents">
      <h2 className="block-title" id="documents">
        {sv.documents.title}
      </h2>
      {message ? (
        <p className="notice" data-state="done" role="status">
          {message}
        </p>
      ) : null}
      <Table
        caption={sv.documents.title}
        isEmpty={(docs.data?.length ?? 0) === 0}
        empty={sv.documents.empty}
        hasOptional
      >
        <thead>
          <tr>
            <th scope="col">{sv.documents.file}</th>
            <th scope="col" className="col-opt">
              {sv.documents.description}
            </th>
            <th scope="col" className="r col-opt">
              {sv.columns.count}
            </th>
            <th scope="col" className="col-opt">
              {sv.columns.date}
            </th>
            {canWrite ? <th scope="col">{sv.columns.action}</th> : null}
          </tr>
        </thead>
        <tbody>
          {docs.data?.map((d) => (
            <tr key={d.id}>
              <td>
                <a
                  href={`/api/v1/documents/${d.id}`}
                  download={d.fileName}
                  aria-label={sv.documents.download(d.fileName)}
                >
                  {d.fileName}
                </a>
              </td>
              <td className="col-opt">{d.description ?? ''}</td>
              <td className="r col-opt">{size(d.sizeBytes)}</td>
              <td className="col-opt num">{moment(d.createdAt)}</td>
              {canWrite ? (
                <td>
                  <button
                    className="btn btn-quiet"
                    type="button"
                    aria-label={sv.documents.remove(d.fileName)}
                    onClick={() => remove.mutate(d.id)}
                  >
                    {sv.documents.removeShort}
                  </button>
                </td>
              ) : null}
            </tr>
          ))}
        </tbody>
      </Table>
      {canWrite ? (
        <form
          className="form-block"
          aria-labelledby="upload"
          onSubmit={(e) => {
            e.preventDefault();
            if (file) upload.mutate();
          }}
        >
          <h3 className="form-heading" id="upload">
            {sv.documents.upload}
          </h3>
          <div className={error ? 'field is-invalid' : 'field'}>
            <label className="label" htmlFor="doc-file">
              {sv.documents.file}
            </label>
            <span className="help" id="doc-file-help">
              {sv.documents.fileHelp}
            </span>
            <input
              className="input"
              id="doc-file"
              type="file"
              aria-describedby="doc-file-help"
              aria-invalid={error ? true : undefined}
              onChange={(e) => setFile(e.target.files?.[0] ?? null)}
            />
            {error ? (
              <p className="error" role="alert">
                {error}
              </p>
            ) : null}
          </div>
          <TextField
            id="doc-description"
            label={sv.documents.description}
            value={description}
            onChange={setDescription}
          />
          <div className="form-actions">
            <button
              className="btn"
              type="submit"
              disabled={!file || upload.isPending}
              aria-busy={upload.isPending}
            >
              {sv.documents.uploadButton}
            </button>
          </div>
        </form>
      ) : null}
    </section>
  );
}
